import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Line } from 'react-chartjs-2';
import {
    Dialog,
    DialogContent,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Card,
    CircularProgress,
    Chip,
    Icon,
    Paper,
    Box,
    Divider,
    IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import * as Actions from '../../store/actions';

const useStyles = makeStyles(theme => ({
    dialog: {
        '& .MuiDialog-paper': {
            minWidth: '1200px',
            minHeight: '800px',
            maxHeight: '90vh'
        }
    },
    dialogHeader: {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '48px',
        gap: theme.spacing(2)
    },
    multipleTestsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        maxWidth: '400px',
        overflowX: 'auto',
        overflowY: 'hidden',
        '&::-webkit-scrollbar': {
            height: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '2px',
        }
    },
        singleTestIndicator: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            padding: theme.spacing(0.5, 1.5),
            borderRadius: theme.spacing(0.5),
            fontSize: '0.8rem',
            minWidth: '160px',
            maxWidth: '180px',
            whiteSpace: 'nowrap',
            flexShrink: 0
        },
        runningSpinner: {
            color: 'white',
        },
        testTextContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center'
        },
        runningText: {
            fontWeight: 'bold',
            fontSize: '0.875rem'
        },
        runningSubtext: {
            fontSize: '0.75rem',
            opacity: 0.8
        },
    testTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center'
    },
    runningSpinner: {
        color: 'white',
        animation: '$spin 2s linear infinite'
    },
    runningText: {
        fontWeight: 'bold',
        fontSize: '0.875rem'
    },
    runningSubtext: {
        fontSize: '0.75rem',
        opacity: 0.8
    },
    '@keyframes spin': {
        '0%': {
            transform: 'rotate(0deg)'
        },
        '100%': {
            transform: 'rotate(360deg)'
        }
    },
    testRunningChip: {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        fontSize: '0.75rem',
        height: '24px'
    },
    errorText: {
        color: theme.palette.error.main
    },
    chartContainer: {
        height: 200,
        marginTop: theme.spacing(2)
    },
    configSection: {
        marginBottom: theme.spacing(2)
    },
    runningTestChip: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
        animation: '$pulse 2s infinite'
    },
    '@keyframes pulse': {
        '0%': {
            opacity: 1,
        },
        '50%': {
            opacity: 0.5,
        },
        '100%': {
            opacity: 1,
        },
    },
    testResults: {
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2)
    }
}));

const GPUStressDialog = ({ open, onClose, clusterId }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    
    // Redux state
    const clusterState = useSelector(({ clusters }) => clusters?.cluster || {});
    const {
        stressTests = {
            data: null,
            history: null,
            loading: false,
            error: null
        },
        clusterMetrics = { data: null }
    } = clusterState;
    
    const {
        data: stressTestData,
        history: stressTestHistory,
        loading: stressTestLoading,
        error: stressTestError
    } = stressTests;
    
    const { data: metricsData } = clusterMetrics;

    // Local state
    const [selectedNode, setSelectedNode] = useState('');
    const [availableNodes, setAvailableNodes] = useState([]);
    const [testDuration, setTestDuration] = useState(60);
    const [testIntensity, setTestIntensity] = useState('moderate');
    const [testType, setTestType] = useState('compute');
    const [testMethod, setTestMethod] = useState('pytorch'); // pytorch, cupy, nvidia-ml-py, fallback
    const [runningTests, setRunningTests] = useState(new Map()); // Map of nodeId -> {nodeName, startTime, testConfig}
    const [connectionStatus, setConnectionStatus] = useState('connected'); // connected, degraded, failed
    const [pollingErrors, setPollingErrors] = useState(0);
    const [lastDataUpdate, setLastDataUpdate] = useState(Date.now()); // Force re-render trigger
    
    // Ref to track if component is mounted
    const isMountedRef = useRef(true);
    // Ref to ensure we only seed animated metrics once per open
    const hasInitializedRef = useRef(false);
    // Per-node polling timers to avoid stale timeouts between runs
    const pollingTimersRef = useRef(new Map()); // node -> timeoutId
    
    // Animation refs
    const animationFrameRef = useRef(null);
    const animationStartTime = useRef(null);
    
    // Animation state for smooth gauge transitions
    const [animatedMetrics, setAnimatedMetrics] = useState({
        gpuUtilization: 0,
        memoryUtilization: 0,
        temperature: 0,
        power: 0
    });

    // Detect available GPU nodes
    useEffect(() => {
        if (metricsData?.nodes?.length > 0) {
            const gpuNodes = metricsData.nodes.filter(node => {
                // Check if node has GPU data - looking for the correct structure
                const hasGpuData = node?.system?.gpu && (
                    Array.isArray(node.system.gpu) ? 
                        node.system.gpu.length > 0 && node.system.gpu.some(gpu => gpu.available && gpu.count > 0) :
                        typeof node.system.gpu === 'object' && node.system.gpu !== null && 
                        node.system.gpu.available && node.system.gpu.count > 0
                );
                
                return hasGpuData;
            }).map(node => ({
                hostname: node.hostname,
                gpuInfo: Array.isArray(node.system.gpu) ? 
                    node.system.gpu : [node.system.gpu]
            }));
            
            setAvailableNodes(gpuNodes);
            
            // Auto-select first node if none selected
            if (gpuNodes.length > 0 && !selectedNode) {
                setSelectedNode(gpuNodes[0].hostname);
            }
        } else {
            setAvailableNodes([]);
        }
    }, [metricsData, selectedNode]);

    // Load cluster metrics on mount
    useEffect(() => {
        if (open && clusterId) {
            dispatch(Actions.fetchClusterMetrics(clusterId));
            // Load stress test history
            dispatch(Actions.fetchGpuStressTestHistory(clusterId));
        }
    }, [open, clusterId, dispatch]);

    // Load test history when node selection changes
    useEffect(() => {
        if (selectedNode && clusterId && open) {
            dispatch(Actions.fetchGpuStressTestHistory(clusterId, { 
                testType: 'gpu',
                nodes: [selectedNode],
                limit: 10,
                _cacheBust: Date.now()
            }));
        }
    }, [selectedNode, clusterId, open, dispatch]);

    // Trigger re-render when stress test history updates
    useEffect(() => {
        if (stressTestHistory) {
            setLastDataUpdate(Date.now());
        }
    }, [stressTestHistory]);
    useEffect(() => {
        if (open && clusterId && selectedNode) {
            // Refresh stress test history when node changes
            dispatch(Actions.fetchGpuStressTestHistory(clusterId));
        }
    }, [selectedNode, open, clusterId, dispatch]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            // Clear any outstanding polling timers on unmount
            try {
                pollingTimersRef.current.forEach(id => clearTimeout(id));
                pollingTimersRef.current.clear();
            } catch (e) {
                // no-op
            }
        };
    }, []);

    // Get current node data
    const getCurrentNodeData = () => {
        if (!metricsData?.nodes || !selectedNode) return null;
        return metricsData.nodes.find(node => node.hostname === selectedNode);
    };

    // Get latest test results for the selected node
    // Get current GPU metrics (same pattern as MemoryBandwidthDialog)
    const getCurrentMetrics = () => {
        // CRITICAL: If any test is running, always return zeros - no exceptions!
    if (runningTests.has(selectedNode)) {
            return { 
                gpuUtilization: 0, 
                memoryUtilization: 0, 
                temperature: 0, 
                power: 0, 
                isFromTest: false 
            };
        }
        
        if (!selectedNode) {
            return { 
                gpuUtilization: 0, 
                memoryUtilization: 0, 
                temperature: 0, 
                power: 0, 
                isFromTest: false 
            };
        }
        
        const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
        
        if (historyData && selectedNode) {
            let nodeRecords = [];
            if (historyData[selectedNode] && Array.isArray(historyData[selectedNode])) {
                nodeRecords = historyData[selectedNode];
            } else if (Array.isArray(historyData)) {
                nodeRecords = historyData.filter(record => 
                    record.hostname === selectedNode || record.node === selectedNode
                );
            }

            if (nodeRecords.length > 0) {
                // GPU results are sorted with latest first (index 0)
                const latestTest = nodeRecords[0];
                const results = latestTest.results || {};
                
                // Check if we have finalMetrics before proceeding
                if (!results.finalMetrics) {
                    return { 
                        gpuUtilization: 0, 
                        memoryUtilization: 0, 
                        temperature: 0, 
                        power: 0, 
                        isFromTest: false 
                    };
                }
                
                const dataTestTime = moment(latestTest.timestamp);
                const dataNow = moment();
                const dataMinutesAgo = dataNow.diff(dataTestTime, 'minutes');
                
                const metrics = {
                    gpuUtilization: results.finalMetrics.gpu_utilization || results.peak_gpu_usage || 0,
                    memoryUtilization: results.finalMetrics.memory_utilization || results.peak_memory_usage || 0,
                    temperature: results.finalMetrics.temperature || results.peak_temperature || 0,
                    power: results.finalMetrics.power_draw || results.peak_power_draw || 0,
                    isFromTest: true,
                    dataAge: dataMinutesAgo < 60 ? dataMinutesAgo : null
                };
                
                return metrics;
            }
        }
        
        return { 
            gpuUtilization: 0, 
            memoryUtilization: 0, 
            temperature: 0, 
            power: 0, 
            isFromTest: false 
        };
    };

    const currentMetrics = React.useMemo(() => {
        return getCurrentMetrics();
    }, [stressTestHistory, selectedNode, lastDataUpdate, runningTests, open]);

    // Check if test is running for current node
    const isTestRunning = runningTests.has(selectedNode);

    // One-time initializer per dialog open: seed animated metrics once when dialog opens
    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
            return;
        }
        if (open && !hasInitializedRef.current && currentMetrics && isMountedRef.current) {
            setAnimatedMetrics({
                gpuUtilization: currentMetrics.gpuUtilization,
                memoryUtilization: currentMetrics.memoryUtilization,
                temperature: currentMetrics.temperature,
                power: currentMetrics.power
            });
            hasInitializedRef.current = true;
        }
    }, [open, currentMetrics]);

    // Animation effect - watch for metrics changes and animate to new values (same as MemoryBandwidthDialog)
    useEffect(() => {
        // CRITICAL: During test execution, completely skip all data processing and animation
        // This prevents any flashing of old values every 15 seconds during periodic data fetches
    if (runningTests.has(selectedNode)) {
            return; // Do absolutely nothing during test execution
        }

        const metrics = getCurrentMetrics();
        
        // Only animate if we have meaningful changes and component is mounted
        if (isMountedRef.current) {
            const hasSignificantChange = 
                Math.abs(metrics.gpuUtilization - animatedMetrics.gpuUtilization) > 1 ||
                Math.abs(metrics.memoryUtilization - animatedMetrics.memoryUtilization) > 1 ||
                Math.abs(metrics.temperature - animatedMetrics.temperature) > 1 ||
                Math.abs(metrics.power - animatedMetrics.power) > 1;

            if (hasSignificantChange) {
                // Different durations: faster for going to 0, normal speed for increasing
                const isResetting = metrics.gpuUtilization === 0 && metrics.temperature === 0;
                const duration = isResetting ? 600 : 1200; // Much faster for reset (0.6s), slower for increases (1.2s)
                
                animateToNewValues({
                    gpuUtilization: metrics.gpuUtilization,
                    memoryUtilization: metrics.memoryUtilization,
                    temperature: metrics.temperature,
                    power: metrics.power
                }, duration);
            }
        }
    }, [lastDataUpdate, selectedNode, isTestRunning, runningTests.size]); // Include running test dependencies

    // Cleanup animation on unmount (same as MemoryBandwidthDialog)
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Animation function for smooth gauge transitions (same as MemoryBandwidthDialog)
    const animateToNewValues = (targetMetrics, duration = 2000) => {
        // CRITICAL: Refuse to animate during test execution (except for initial reset to 0)
    const isInitialReset = targetMetrics.gpuUtilization === 0 && targetMetrics.temperature === 0;
    if (runningTests.has(selectedNode) && !isInitialReset) {
            return; // Don't animate to any values except zeros during test
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        const startValues = { ...animatedMetrics };
        animationStartTime.current = performance.now();
        
        const animate = (currentTime) => {
            if (!isMountedRef.current) return;
            
            const elapsed = currentTime - animationStartTime.current;
            const progress = Math.min(elapsed / duration, 1);
            
            // Different easing for increasing vs decreasing values (like car tachometer)
            const isDecreasing = Object.keys(targetMetrics).some(key => 
                targetMetrics[key] < startValues[key]
            );
            
            let easedProgress;
            if (isDecreasing) {
                // Smooth deceleration for decreasing values (like releasing gas pedal)
                easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
            } else {
                // Slightly bouncy for increasing values (like pressing gas pedal)
                easedProgress = progress < 0.5 
                    ? 2 * progress * progress 
                    : 1 - Math.pow(-2 * progress + 2, 2) / 2; // Ease-in-out
            }
            
            const newAnimatedMetrics = {};
            Object.keys(targetMetrics).forEach(key => {
                const start = startValues[key] || 0;
                const end = targetMetrics[key];
                newAnimatedMetrics[key] = start + (end - start) * easedProgress;
            });
            
            setAnimatedMetrics(newAnimatedMetrics);
            
            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };
        
        animationFrameRef.current = requestAnimationFrame(animate);
    };

    const handleExecuteTest = async () => {
        if (!selectedNode || stressTestLoading) return;

        const testConfig = {
            nodes: [selectedNode],
            duration: testDuration,
            intensity: testIntensity,
            testType: testType,
            testMethod: testMethod
        };

        // Clear any existing polling timer for this node before starting a new run
        try {
            const existingTimer = pollingTimersRef.current.get(selectedNode);
            if (existingTimer) {
                clearTimeout(existingTimer);
                pollingTimersRef.current.delete(selectedNode);
            }
        } catch (e) {
            // no-op
        }

        // Add to running tests
        setRunningTests(prev => new Map(prev.set(selectedNode, {
            nodeName: selectedNode,
            startTime: Date.now(),
            duration: testDuration,
            status: 'running',
            config: testConfig,
            id: null // will fill if backend returns id
        })));

        // Stop any ongoing animations immediately when starting test
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // Immediately set animated metrics to 0 when starting test
        setAnimatedMetrics({
            gpuUtilization: 0,
            memoryUtilization: 0,
            temperature: 0,
            power: 0
        });

        // Also animate gauges to 0 quickly when starting new test (same as MemoryBandwidthDialog)
        animateToNewValues({
            gpuUtilization: 0,
            memoryUtilization: 0,
            temperature: 0,
            power: 0
        }, 800); // Fast 0.8 second animation to 0

        try {
            const testResult = await dispatch(Actions.runGpuStressTest(clusterId, testConfig));

            // Check for cluster not found error and handle gracefully
            if (testResult?.payload?.message?.includes("Cluster") && testResult?.payload?.message?.includes("not found")) {
                // Remove this test from running tests since we can't proceed
                setRunningTests(prevRunningTests => {
                    const updatedRunningTests = new Map(prevRunningTests);
                    updatedRunningTests.delete(selectedNode);
                    return updatedRunningTests;
                });
                return;
            }
            
            // Store the timestamp when test started for detecting new results
            const testStartTime = moment();
            // Try to capture an id from the response if available
            const possibleId = testResult?.jobId || testResult?.id || testResult?.requestId || testResult?.data?.jobId || null;
            if (possibleId && isMountedRef.current) {
                setRunningTests(prev => {
                    const next = new Map(prev);
                    const item = next.get(selectedNode);
                    if (item) {
                        next.set(selectedNode, { ...item, id: possibleId });
                    }
                    return next;
                });
            }
            
            // Intelligent polling strategy - poll until we get fresh results for this specific node
            let pollingAttempts = 0;
            const maxPollingMinutes = 20; // Maximum 20 minutes of polling for longer tests
            const maxPollingAttempts = Math.floor((maxPollingMinutes * 60) / 20); // Poll every 20 seconds average
            
            // Start intelligent polling that checks for test completion
            let currentInterval = 20000; // Start with 20 seconds
            let testCompleted = false;
            
            const checkForResults = async () => {
                try {
                    
                    // Try to fetch GPU stress test history directly and check for new results
                    const historyResult = await dispatch(Actions.fetchGpuStressTestHistory(clusterId, { 
                        testType: 'gpu', 
                        limit: 50,
                        nodes: [selectedNode],
                        _cacheBust: Date.now() 
                    }));
                                        
                    // Check if we have fresh results for this node
                    if (historyResult?.data?.history || historyResult?.history) {
                        const historyData = historyResult.data?.history || historyResult.history;
                        const nodeHistory = historyData[selectedNode];

                        const timeSkewToleranceSeconds = 5; // allow small clock drift, not 1 minute

                        const matchesCurrentRun = (record) => {
                            try {
                                const recId = record?.jobId || record?.id || record?.requestId || null;
                                if (possibleId && recId && String(recId) === String(possibleId)) return true;

                                // Fallback: compare config fields when available
                                const params = record?.parameters || {};
                                const sameMethod = params?.testMethod ? params.testMethod === testConfig.testMethod : true;
                                const sameIntensity = params?.testIntensity ? params.testIntensity === testConfig.intensity : true;
                                const sameDuration = params?.duration ? Number(params.duration) === Number(testConfig.duration) : true;
                                return sameMethod && sameIntensity && sameDuration;
                            } catch (e) {
                                return false;
                            }
                        };

                        if (nodeHistory && Array.isArray(nodeHistory) && nodeHistory.length > 0) {
                            const sortedHistory = [...nodeHistory].sort((a, b) =>
                                moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf()
                            );

                            // Find the first record that matches this exact run (by id/config) and is not older than the start time (with tiny tolerance)
                            const matching = sortedHistory.find(r => {
                                const resultTime = moment(r.timestamp);
                                const diffSec = resultTime.diff(testStartTime, 'seconds');
                                return matchesCurrentRun(r) && diffSec >= -timeSkewToleranceSeconds;
                            });

                            if (matching) {
                                const resultTime = moment(matching.timestamp);

                                if (isMountedRef.current) {
                                    setRunningTests(prevRunningTests => {
                                        const updatedRunningTests = new Map(prevRunningTests);
                                        updatedRunningTests.delete(selectedNode);
                                        return updatedRunningTests;
                                    });
                                    setPollingErrors(0);

                                    // Clear any scheduled timer for this node
                                    const tId = pollingTimersRef.current.get(selectedNode);
                                    if (tId) {
                                        clearTimeout(tId);
                                        pollingTimersRef.current.delete(selectedNode);
                                    }

                                    // Refresh the stress test history for this node
                                    dispatch(Actions.fetchGpuStressTestHistory(clusterId, {
                                        testType: 'gpu',
                                        limit: 50,
                                        nodes: [selectedNode],
                                        _cacheBust: Date.now()
                                    }));
                                }

                                testCompleted = true;
                                return true;
                            } 
                        }
                    }
                    
                    return false;
                } catch (error) {
                    console.warn('Error checking for GPU stress test results:', error.message);
                    
                    // Only update state if component is still mounted
                    if (!isMountedRef.current) {
                        return false;
                    }
                    
                    const errorCount = pollingErrors + 1;
                    setPollingErrors(errorCount);
                    return false;
                }
            };
            
            // Start polling
            const pollForResults = () => {
                checkForResults().then(completed => {
                    if (!completed && !testCompleted && pollingAttempts < maxPollingAttempts) {
                        pollingAttempts++;
                        const nextId = setTimeout(pollForResults, currentInterval);
                        // Track timer per node to allow cancellation
                        pollingTimersRef.current.set(selectedNode, nextId);
                        // Gradually increase interval to reduce server load, but not too much
                        if (pollingAttempts > 3) currentInterval = Math.min(currentInterval * 1.05, 45000); // Max 45 seconds
                    } else if (!completed && pollingAttempts >= maxPollingAttempts) {
                        console.warn(`GPU stress test polling timed out after ${maxPollingMinutes} minutes for node ${selectedNode}`);
                        // Remove from running tests on timeout
                        setRunningTests(prevRunningTests => {
                            const updatedRunningTests = new Map(prevRunningTests);
                            updatedRunningTests.delete(selectedNode);
                            return updatedRunningTests;
                        });
                        // Clear timer ref for this node
                        const tId = pollingTimersRef.current.get(selectedNode);
                        if (tId) {
                            clearTimeout(tId);
                            pollingTimersRef.current.delete(selectedNode);
                        }
                    }
                });
            };
            
            // Start polling after sufficient delay for test to initialize and begin
            const firstId = setTimeout(pollForResults, 15000); // Wait 15 seconds before first poll
            pollingTimersRef.current.set(selectedNode, firstId);

        } catch (error) {
            console.error('GPU stress test failed:', error);
            setRunningTests(prev => {
                const newMap = new Map(prev);
                newMap.delete(selectedNode);
                return newMap;
            });
            // Clear timer if any
            const tId = pollingTimersRef.current.get(selectedNode);
            if (tId) {
                clearTimeout(tId);
                pollingTimersRef.current.delete(selectedNode);
            }
        }
    };

    const handleStopTest = () => {
        if (!selectedNode) return;
        
        dispatch(Actions.stopGpuStressTest(clusterId, selectedNode))
            .then(() => {
                setRunningTests(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(selectedNode);
                    return newMap;
                });
                // Clear any outstanding polling timer for this node
                const tId = pollingTimersRef.current.get(selectedNode);
                if (tId) {
                    clearTimeout(tId);
                    pollingTimersRef.current.delete(selectedNode);
                }
            });
    };

    // Create chart data from time series
    const createChartData = (timeSeriesData) => {
        if (!timeSeriesData || timeSeriesData.length === 0) {
            return null;
        }

        const labels = timeSeriesData.map((point, index) => {
            if (point.elapsed !== undefined) {
                return `${Math.round(point.elapsed)}s`;
            }
            return `${index * 2}s`; // Fallback: assume 2s intervals
        });

        return {
            labels,
            datasets: [
                {
                    label: 'GPU Utilization (%)',
                    data: timeSeriesData.map(point => point.gpuUtilization || 0),
                    borderColor: '#2196f3',
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    fill: true,
                    lineTension: 0.4,
                    yAxisID: 'y',
                },
                {
                    label: 'Memory Utilization (%)',
                    data: timeSeriesData.map(point => point.memoryUtilization || 0),
                    borderColor: '#4caf50',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    fill: true,
                    lineTension: 0.4,
                    yAxisID: 'y',
                },
                {
                    label: 'Temperature (°C)',
                    data: timeSeriesData.map(point => point.temperature || 0),
                    borderColor: '#ff9800',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    fill: false,
                    lineTension: 0.4,
                    yAxisID: 'y1',
                },
                {
                    label: 'Power Draw (W)',
                    data: timeSeriesData.map(point => point.powerDraw || 0),
                    borderColor: '#e91e63',
                    backgroundColor: 'rgba(233, 30, 99, 0.1)',
                    fill: false,
                    lineTension: 0.4,
                    yAxisID: 'y1',
                }
            ]
        };
    };

    // Chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'top',
        },
        title: {
            display: true,
            text: 'GPU Performance Over Time'
        },
        scales: {
            xAxes: [{
                display: true,
                scaleLabel: {
                    display: true,
                    labelString: 'Time'
                }
            }],
            yAxes: [{
                id: 'y',
                type: 'linear',
                display: true,
                position: 'left',
                scaleLabel: {
                    display: true,
                    labelString: 'Utilization (%)'
                },
                ticks: {
                    min: 0,
                    max: 100
                }
            }, {
                id: 'y1',
                type: 'linear',
                display: true,
                position: 'right',
                scaleLabel: {
                    display: true,
                    labelString: 'Temperature (°C) / Power (W)'
                },
                ticks: {
                    min: 0
                },
                gridLines: {
                    drawOnChartArea: false,
                }
            }]
        },
        tooltips: {
            mode: 'index',
            intersect: false,
        },
        elements: {
            point: {
                radius: 2,
                hoverRadius: 6
            }
        }
    };

    // Get latest test results for current node
    const getLatestResults = () => {
        // CRITICAL: If any test is running on this node, return null - no exceptions!
        if (runningTests.has(selectedNode)) {
            return null;
        }
        
        if (!selectedNode) return null;
        
        const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
        if (!historyData) return null;
        
        let nodeRecords = [];
        if (historyData[selectedNode] && Array.isArray(historyData[selectedNode])) {
            nodeRecords = historyData[selectedNode];
        } else if (Array.isArray(historyData)) {
            nodeRecords = historyData.filter(record => 
                record.hostname === selectedNode || record.node === selectedNode
            );
        }
        
        if (nodeRecords.length > 0) {
            // Sort by timestamp descending and return the most recent
            const sortedHistory = [...nodeRecords].sort((a, b) => 
                moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf()
            );
            return sortedHistory[0];
        }
        
        return null;
    };

    const latestResults = getLatestResults();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            className={classes.dialog}
        >
            {/* Custom Header */}
            <div className={classes.dialogHeader}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icon style={{ color: '#ff9800', marginRight: '8px' }}>memory</Icon>
                    <Typography variant="h6" style={{ fontWeight: '500' }}>
                        GPU Stress Test
                        {runningTests.size > 0 && (
                            <span style={{ color: '#1976d2', marginLeft: '8px', fontSize: '0.8em' }}>
                                ({runningTests.size} running)
                            </span>
                        )}
                    </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {runningTests.size > 0 && (
                        <div className={classes.multipleTestsContainer}>
                            {Array.from(runningTests.entries()).map(([nodeId, testInfo]) => {
                                const elapsed = Math.floor((Date.now() - testInfo.startTime) / 1000);
                                
                                return (
                                    <div key={nodeId} className={classes.singleTestIndicator}>
                                        <CircularProgress size={16} className={classes.runningSpinner} />
                                        <div className={classes.testTextContainer}>
                                            <div className={classes.runningText}>
                                                {testInfo.nodeName}
                                            </div>
                                            <div className={classes.runningSubtext}>
                                                {elapsed}s elapsed
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    <IconButton onClick={onClose} size="small">
                        <Icon>close</Icon>
                    </IconButton>
                </div>
            </div>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Configuration Section - Left Side */}
                    <Grid item xs={12} md={4}>
                        <Paper className="p-16">
                            <Typography variant="h6" className="mb-16">
                                Test Configuration
                            </Typography>

                            <div className={classes.configSection}>
                                <FormControl fullWidth size="small" margin="normal">
                                    <InputLabel>GPU Node</InputLabel>
                                    <Select
                                        value={selectedNode}
                                        onChange={(e) => setSelectedNode(e.target.value)}
                                        label="GPU Node"
                                        disabled={stressTestLoading || isTestRunning || availableNodes.length === 0}
                                    >
                                        {availableNodes.length > 0 ? (
                                            availableNodes.map(node => (
                                                <MenuItem key={node.hostname} value={node.hostname}>
                                                    <div>
                                                        <Typography variant="body2">{node.hostname}</Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {node.gpuInfo.reduce((total, gpu) => total + (gpu.count || 0), 0)} GPU{node.gpuInfo.reduce((total, gpu) => total + (gpu.count || 0), 0) !== 1 ? 's' : ''} - {node.gpuInfo[0]?.gpus?.[0]?.name || 'GPU'}
                                                        </Typography>
                                                    </div>
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled value="">
                                                <div>
                                                    <Typography variant="body2" color="textSecondary">
                                                        No GPU nodes available
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        This cluster doesn't have any nodes with GPUs
                                                    </Typography>
                                                </div>
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </div>

                            <Divider className="my-16" />

                            <div className={classes.configSection}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Duration (seconds)"
                                            type="number"
                                            value={testDuration}
                                            onChange={(e) => setTestDuration(Math.max(1, parseInt(e.target.value) || 1))}
                                            disabled={stressTestLoading || isTestRunning}
                                            inputProps={{ min: 1, max: 3600 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Test Type</InputLabel>
                                            <Select
                                                value={testType}
                                                onChange={(e) => setTestType(e.target.value)}
                                                label="Test Type"
                                                disabled={stressTestLoading || isTestRunning}
                                            >
                                                <MenuItem value="compute">Compute</MenuItem>
                                                <MenuItem value="memory">Memory</MenuItem>
                                                <MenuItem value="mixed">Mixed</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Test Intensity</InputLabel>
                                            <Select
                                                value={testIntensity}
                                                onChange={(e) => setTestIntensity(e.target.value)}
                                                label="Test Intensity"
                                                disabled={stressTestLoading || isTestRunning}
                                            >
                                                <MenuItem value="light">Light (25%)</MenuItem>
                                                <MenuItem value="moderate">Moderate (50%)</MenuItem>
                                                <MenuItem value="heavy">Heavy (75%)</MenuItem>
                                                <MenuItem value="extreme">Extreme (100%)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Test Method</InputLabel>
                                            <Select
                                                value={testMethod}
                                                onChange={(e) => setTestMethod(e.target.value)}
                                                label="Test Method"
                                                disabled={stressTestLoading || isTestRunning}
                                            >
                                                <MenuItem value="pytorch">PyTorch CUDA (Recommended)</MenuItem>
                                                <MenuItem value="cupy">CuPy GPU Arrays</MenuItem>
                                                <MenuItem value="nvidia-ml-py">NVIDIA-ML Queries</MenuItem>
                                                <MenuItem value="auto">Auto-detect Best Method</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </div>
                        </Paper>
                    </Grid>

                    {/* GPU Metrics Display - Right Side */}
                    <Grid item xs={12} md={8}>
                        <Paper className="p-16">
                            {/* Speedtest-Style Gauges */}
                            <Grid container spacing={3}>
                                {/* Main GPU Utilization Gauge */}
                                <Grid item xs={12} md={6}>
                                    <Card className="p-16 text-center" style={{ minHeight: '212px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
                                            <Typography variant="h6">GPU Utilization</Typography>
                                            <Typography variant="caption" style={{ 
                                                color: runningTests.has(selectedNode) ? '#4caf50' : (currentMetrics.isFromTest ? '#4caf50' : '#ff9800'),
                                                fontSize: '10px',
                                                fontWeight: 'bold'
                                            }}>
                                                {runningTests.has(selectedNode)
                                                    ? 'GPU STRESS TEST (running)'
                                                    : (currentMetrics.isFromTest 
                                                        ? `GPU STRESS TEST ${currentMetrics.dataAge ? `(${currentMetrics.dataAge}m ago)` : ''}` 
                                                        : 'LIVE MONITORING')}
                                            </Typography>
                                        </div>
                                        <div className="relative">
                                            <svg width="180" height="100" viewBox="0 0 180 100" className="mx-auto">
                                                {/* Background Arc */}
                                                <path
                                                    d="M 20 80 A 70 70 0 0 1 160 80"
                                                    stroke="#e0e0e0"
                                                    strokeWidth="8"
                                                    fill="none"
                                                />
                                                {/* Progress Arc */}
                                                <path
                                                    d="M 20 80 A 70 70 0 0 1 160 80"
                                                    stroke="#ff5722"
                                                    strokeWidth="8"
                                                    fill="none"
                                                    strokeDasharray={`${Math.min(100, animatedMetrics.gpuUtilization) * 2.2} 220`}
                                                    style={{ transition: 'stroke-dasharray 0.1s ease' }}
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center" style={{ top: '0px' }}>
                                                <div>
                                                    <Typography variant="h4" style={{ color: '#ff5722', fontWeight: 'bold' }}>
                                                        {Math.round(animatedMetrics.gpuUtilization)}%
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        USAGE
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </Grid>

                                {/* Secondary Metrics */}
                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" style={{ color: '#2196f3', fontWeight: 'bold' }}>
                                                    {Math.round(animatedMetrics.memoryUtilization)}%
                                                </Typography>
                                                <Typography variant="caption">GPU Memory</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-blue-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, animatedMetrics.memoryUtilization)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                                    {Math.round(animatedMetrics.temperature)}°C
                                                </Typography>
                                                <Typography variant="caption">Temperature</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-orange-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.temperature / 90) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                                    {Math.round(animatedMetrics.power)}W
                                                </Typography>
                                                <Typography variant="caption">Power Draw</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-green-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.power / 250) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" style={{ color: '#9c27b0', fontWeight: 'bold' }}>
                                                    {runningTests.has(selectedNode) ? '--' : (
                                                        latestResults ? 
                                                            (latestResults.results?.status === 'completed' ? 'PASS' : 
                                                             latestResults.results?.status === 'failed' ? 'FAIL' : 
                                                             latestResults.results?.status?.toUpperCase() || 'PASS') : 
                                                            '--')}
                                                </Typography>
                                                <Typography variant="caption">Test Status</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    {!runningTests.has(selectedNode) && (
                                                        <div 
                                                            className={`h-2 rounded-full transition-all duration-100 ${
                                                                latestResults?.results?.status === 'failed' ? 'bg-red-500' : 'bg-purple-500'
                                                            }`}
                                                            style={{ width: `${latestResults ? '100' : '0'}%` }}
                                                        ></div>
                                                    )}
                                                </div>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* GPU Test History Chart */}
                            <div className={classes.chartContainer}>
                                {selectedNode ? (
                                    <Paper className="p-16" style={{ height: '100%' }}>
                                        
                                        {latestResults ? (
                                            <Box>
                                                {/* Show time series chart if available */}
                                                {latestResults.results?.timeSeriesData && latestResults.results.timeSeriesData.length > 0 ? (
                                                    <Box>
                                                        {/* Time series chart only */}
                                                        <div style={{ height: '300px' }}>
                                                            <Line data={createChartData(latestResults.results.timeSeriesData)} options={chartOptions} />
                                                        </div>
                                                        
                                                        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                                                            Duration: {Math.round(latestResults.results?.actualDuration || 0)}s | 
                                                            Data Points: {latestResults.results?.dataPoints || latestResults.results.timeSeriesData.length} | 
                                                            Method: {latestResults.parameters?.testMethod || 'auto'} | 
                                                            Intensity: {latestResults.parameters?.testIntensity || 'moderate'}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="textSecondary">Status:</Typography>
                                                                <Typography variant="body2">{latestResults.results?.status || 'Unknown'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="textSecondary">Duration:</Typography>
                                                                <Typography variant="body2">{latestResults.results?.actualDuration ? `${Math.round(latestResults.results.actualDuration)}s` : 'N/A'}</Typography>
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="textSecondary">Peak GPU:</Typography>
                                                                <Typography variant="body2">{latestResults.results?.peak_gpu_usage || latestResults.results?.peakMetrics?.gpu_utilization || 0}%</Typography>
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Typography variant="caption" color="textSecondary">Peak Memory:</Typography>
                                                                <Typography variant="body2">{latestResults.results?.peak_memory_usage || latestResults.results?.peakMetrics?.memory_utilization || 0}%</Typography>
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="textSecondary">Peak Power:</Typography>
                                                                <Typography variant="body2">{latestResults.results?.peak_power_draw || latestResults.results?.peakMetrics?.power_draw || 0}W</Typography>
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="textSecondary">Peak Temp:</Typography>
                                                                <Typography variant="body2">{latestResults.results?.peak_temperature || latestResults.results?.peakMetrics?.temperature || 0}°C</Typography>
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <Typography variant="caption" color="textSecondary">Test Type:</Typography>
                                                                <Typography variant="body2">{latestResults.testType || 'GPU'}</Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : (
                                            isTestRunning ? (
                                                <Box>
                                                    <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', border: '1px dashed #ddd', borderRadius: 4 }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <CircularProgress size={24} style={{ marginBottom: 8 }} />
                                                            <Typography variant="body2" color="textSecondary">
                                                                Test running... collecting GPU performance data
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                The chart will appear when the test completes
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    No test results available. Run a GPU stress test to see performance data.
                                                </Typography>
                                            )
                                        )}
                                    </Paper>
                                ) : (
                                    <Paper className="p-16" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Select a GPU node to view performance metrics
                                        </Typography>
                                    </Paper>
                                )}
                            </div>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Error Display */}
                {stressTestError && (
                    <Grid item xs={12}>
                        <Paper className="p-16 bg-red-50">
                            <Typography variant="body2" className={classes.errorText}>
                                <Icon className="mr-8">error</Icon>
                                {stressTestError.message || stressTestError}
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </DialogContent>

            {/* Action Buttons */}
            <div style={{ padding: '16px 24px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleExecuteTest}
                    color="primary"
                    variant="contained"
                    disabled={!selectedNode || stressTestLoading || isTestRunning || availableNodes.length === 0}
                    startIcon={isTestRunning ? <CircularProgress size={16} /> : <Icon>play_arrow</Icon>}
                >
                    {availableNodes.length === 0 ? 
                        'No GPU Nodes Available' :
                        isTestRunning ? 
                            `Test Running on ${selectedNode}` : 
                            `Start GPU Stress Test`
                    }
                </Button>
            </div>
        </Dialog>
    );
};

export default GPUStressDialog;

